<script setup lang="ts">
/**
 * Carte produit de la grille vitrine `/boutique/[slug]`.
 *
 * ⚠️ DÉCISION DE REFONTE — LA CARTE N'EST PLUS ENCARTÉE.
 * Elle était un bloc blanc (fond + bordure + ombre au survol) posé DANS une
 * autre carte blanche (le catalogue), elle-même sur un fond bleuté : trois
 * niveaux de surface pour une seule information. La fiche produit a déjà
 * tranché — l'information flotte, la carte est réservée à l'action — et
 * « Vous aimerez aussi » applique déjà ce canon en bas de fiche.
 *
 * Ce qui reste ENCARTÉ ici : la PHOTO seule (`rounded-2xl` + aplat de repli).
 * C'est elle qui fait la vitrine marchande — Shopify, Sézane, Decathlon
 * n'entourent pas leurs vignettes d'un cadre blanc, ils laissent l'image porter
 * le bloc et posent le texte NU dessous. Le texte hérite donc du fond de page,
 * ce qui rend la grille nettement plus aérée à densité égale.
 *
 * ⚠️ ACQUIS CONSERVÉS À L'IDENTIQUE (aucun n'est négociable) :
 *  - VRAI `<NuxtLink>` vers `shopProductPath` : ces fiches ont été créées pour
 *    être partageables et indexables. Un bouton JS casserait le clic-molette,
 *    « ouvrir dans un nouvel onglet » et le crawl Google ;
 *  - badge « Téléchargement immédiat » sur les produits numériques ;
 *  - libellé de prix via `shopProductPriceLabel` (« À partir de X € ») ;
 *  - badge de stock restant AVEC pulsation temps réel (socket boutique) ;
 *  - état « Épuisé ».
 */
import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import { shopProductPath } from "~/utils/shopProductUrl";
import type { ShopProduct, VariantAxis } from "~/types/shop";
import {
  shopProductPriceLabel,
  shopProductTotalRemaining,
  shopProductSoldOut,
} from "~/utils/shopPricing";
import {
  resolveShopColorSwatch,
  shouldRenderAsColorSwatches,
} from "~/utils/shopColorSwatch";

const props = defineProps<{
  product: ShopProduct;
  showRemainingStock: boolean;
  /** Achat désactivé (hors fenêtre de vente / boutique clôturée). */
  canBuy: boolean;
  /** Slug de la boutique — sert à construire l'URL réelle de la fiche produit. */
  shopSlug: string;
}>();

/** URL propre de la fiche produit (partageable et indexable). */
const productPath = computed(() =>
  shopProductPath(props.shopSlug, props.product),
);

defineEmits<{
  (e: "open"): void;
}>();

const config = useRuntimeConfig();
const assetsBaseUrl = computed(
  () => (config.public.apiUrl as string) || "https://api.donoor.org",
);

const thumbnailUrl = computed(() => {
  const id = props.product.images?.[0];
  if (!id) return null;
  return `${assetsBaseUrl.value}/api/assets/${id}?width=800&height=800&fit=cover&format=webp&quality=80`;
});

/**
 * Seconde photo, révélée au SURVOL sur desktop (canon marchand : Sézane,
 * Uniqlo, Decathlon). Un t-shirt photographié de face ne dit rien de son dos ni
 * de sa coupe portée ; montrer la vue suivante sans quitter la grille évite un
 * aller-retour vers la fiche pour une simple vérification visuelle.
 * `null` quand le produit n'a qu'une image — aucun effet, aucun octet chargé.
 */
const hoverThumbnailUrl = computed(() => {
  const id = props.product.images?.[1];
  if (!id) return null;
  return `${assetsBaseUrl.value}/api/assets/${id}?width=800&height=800&fit=cover&format=webp&quality=80`;
});

const priceLabel = computed(() => shopProductPriceLabel(props.product));
const isSoldOut = computed(() => shopProductSoldOut(props.product));
const totalRemaining = computed(() => shopProductTotalRemaining(props.product));
const showStockBadge = computed(
  () =>
    props.showRemainingStock &&
    !isSoldOut.value &&
    totalRemaining.value !== null &&
    totalRemaining.value <= 30,
);

/** Stock CRITIQUE : bascule le badge en rouge (raréfaction réelle). */
const isLowStock = computed(() => (totalRemaining.value ?? 0) <= 5);

/**
 * Produit numérique : « Téléchargement immédiat » est son argument de vente
 * n°1 (pas de port, pas d'attente). Sans ce repère dans la grille, il est
 * indiscernable d'un t-shirt. `product_type` absent = physique (DEFAULT base,
 * données antérieures à la migration 166).
 */
const isDigital = computed(() => props.product.product_type === "digital");

/**
 * Description courte sous le nom — pattern BslugStepTickets (`tt.description`).
 * `stripHtml` décode les entités, `truncateForMeta` ne coupe jamais un emoji
 * en deux (cf. AslugStepPlans).
 */
const shortDescription = computed(() => {
  const raw = truncateForMeta(stripHtml(props.product.description ?? ""), 90);
  return raw || null;
});

/**
 * Pastilles de coloris — MÊME règle que la fiche produit et « Vous aimerez
 * aussi » (`shopColorSwatch`) : il faut un axe dont le NOM désigne une couleur
 * ET dont TOUTES les valeurs sont reconnues. Une seule valeur inconnue → aucune
 * pastille, jamais un rond gris inventé.
 *
 * Ajoutées à la grille par la refonte : c'est l'information qui décide de
 * l'ouverture d'une fiche (« existe-t-il en noir ? »), et la seule qu'une
 * vignette carrée ne peut pas montrer.
 */
const colorSwatches = computed(() => {
  const axes: VariantAxis[] = props.product.variant_axes ?? [];
  const colorAxis = axes.find((axis) => shouldRenderAsColorSwatches(axis));
  if (!colorAxis) return [];
  return colorAxis.values.flatMap((value) => {
    const swatch = resolveShopColorSwatch(value);
    // Non-null par construction (`shouldRenderAsColorSwatches` exige 100 % de
    // valeurs résolues) ; le garde satisfait le typage sans `!` ni `any`.
    return swatch ? [{ value, hex: swatch.hex, hex2: swatch.hex2 }] : [];
  });
});

/**
 * Aplats PLEINS uniquement (règle `ui_no_bg_transparency`) : une bicolore est
 * peinte en deux moitiés via un `linear-gradient` à arrêt franc — deux couleurs
 * opaques, aucun canal alpha.
 */
function swatchStyle(swatch: { hex: string; hex2: string | null }): Record<string, string> {
  return {
    background: swatch.hex2
      ? `linear-gradient(135deg, ${swatch.hex} 0 50%, ${swatch.hex2} 50% 100%)`
      : swatch.hex,
  };
}

/**
 * Raréfaction perceptible quand le socket boutique patche `remaining`.
 *
 * ⚠️ PAS `useAnimatedNumber` / `useEventAnimatedCounters` ici : /b anime un
 * compteur GLOBAL de places où les sauts sont larges. Sur un badge de grille
 * qui passe de 3 à 2, un count-up se lit comme du tremblement. On marque donc
 * le changement par une pulsation brève — même intention, coût nul.
 */
const stockPulse = ref(false);
let pulseTimer: ReturnType<typeof setTimeout> | null = null;

watch(totalRemaining, (next, prev) => {
  // Uniquement une BAISSE réelle après le 1er rendu (pas le montage initial).
  if (prev === null || next === null || next >= prev) return;
  stockPulse.value = true;
  if (pulseTimer) clearTimeout(pulseTimer);
  pulseTimer = setTimeout(() => {
    stockPulse.value = false;
  }, 900);
});

onUnmounted(() => {
  if (pulseTimer) clearTimeout(pulseTimer);
});
</script>

<template>
  <!--
    VRAI lien (et non un bouton) : la fiche produit a une URL propre, donc la
    carte doit être un `<a>`. Sans ça, ni « ouvrir dans un nouvel onglet », ni
    le clic-molette, ni le crawl Google ne fonctionnent — or ces pages ont été
    créées précisément pour être partagées et indexées.
    Le `@click` est conservé (il pilote la navigation interne + l'embed) mais
    n'empêche pas le comportement natif du lien.
  -->
  <NuxtLink
    :to="productPath"
    class="group block text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400"
    :aria-label="$t('boutique.productCard.ariaVoir', { nom: product.name })"
    @click="$emit('open')"
  >
    <!-- PHOTO CARRÉE — même ratio que la galerie de la fiche produit
         (`BoutiqueProductGallery`, `aspect-square`). Le portrait 4/5 essayé
         auparavant donnait un peu plus de surface, mais recadrait la photo
         DIFFÉREMMENT ici et sur la fiche : l'asso cadrait pour l'un et
         découvrait l'autre rogné (Karim 2026-08-10). Un seul ratio partout =
         ce que l'asso valide au recadrage est ce que l'acheteur voit. -->
    <div class="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 dark:bg-[#1a1a1a]">
      <img
        v-if="thumbnailUrl"
        :src="thumbnailUrl"
        :alt="product.name"
        loading="lazy"
        class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        :class="[
          isSoldOut ? 'grayscale' : '',
          hoverThumbnailUrl ? 'md:group-hover:opacity-0 md:transition-[transform,opacity]' : '',
        ]"
      />
      <div v-else class="w-full h-full flex items-center justify-center">
        <UIcon name="i-lucide-package" class="size-10 text-gray-300 dark:text-gray-600" />
      </div>

      <!-- Seconde vue au survol (desktop uniquement : sur mobile il n'y a pas
           de survol, et charger une image jamais vue serait du gaspillage —
           d'où `hidden md:block` en plus du `loading="lazy"`). -->
      <img
        v-if="hoverThumbnailUrl && !isSoldOut"
        :src="hoverThumbnailUrl"
        alt=""
        aria-hidden="true"
        loading="lazy"
        class="hidden md:block absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      />

      <!-- ÉPUISÉ : bandeau textuel en bas de photo plutôt qu'une pastille de
           coin. Un produit indisponible doit être écarté d'un coup d'œil, pas
           déchiffré. Aplat PLEIN (`bg-[#2d2e5c]`), jamais un voile translucide
           (règle `ui_no_bg_transparency`). -->
      <div
        v-if="isSoldOut"
        class="absolute inset-x-0 bottom-0 bg-[#2d2e5c] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white"
      >
        {{ $t("boutique.commun.epuise") }}
      </div>

      <!-- Stock restant — aplat PLEIN blanc (avant : `bg-white/95`, soit une
           transparence de couleur interdite par `ui_no_bg_transparency`). -->
      <span
        v-else-if="showStockBadge"
        class="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold shadow-[0_1px_3px_rgba(45,46,92,0.12)] tabular-nums transition-transform duration-300"
        :class="[
          isLowStock
            ? 'bg-red-600 text-white'
            : 'bg-white dark:bg-[#141414] text-[#505870] dark:text-gray-300',
          stockPulse ? 'scale-110' : 'scale-100',
        ]"
      >
        <UIcon v-if="isLowStock" name="i-lucide-flame" class="size-3" />
        {{ totalRemaining }} restant{{ (totalRemaining ?? 0) > 1 ? "s" : "" }}
      </span>

      <!-- Produit numérique : argument de vente n°1, visible DÈS la grille.
           Remonté SUR la photo (il était sous le nom) pour tenir la même ligne
           de lecture que le stock, et pour ne pas repousser le prix. -->
      <span
        v-if="isDigital"
        class="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-600 text-white shadow-[0_1px_3px_rgba(45,46,92,0.12)]"
      >
        <UIcon name="i-lucide-download" class="size-3" />
        {{ $t("boutique.commun.telechargementImmediat") }}
      </span>

      <!-- Ajout au panier : pastille d'accent en bas de photo, révélée au
           survol sur desktop et TOUJOURS visible sur mobile (où il n'y a pas de
           survol). Elle NE capture PAS le clic — c'est un repère visuel : la
           sélection de variante (taille, couleur) se fait sur la fiche, et
           ajouter au panier depuis la grille sauterait ce choix. -->
      <span
        v-if="canBuy && !isSoldOut"
        aria-hidden="true"
        class="absolute bottom-2.5 right-2.5 inline-flex items-center justify-center size-10 rounded-full shadow-[0_1px_3px_rgba(45,46,92,0.12)] transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100"
        :style="{ background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' }"
      >
        <UIcon name="i-lucide-arrow-right" class="size-5" />
      </span>
    </div>

    <!-- TEXTE NU sous la photo — aucun fond, aucune bordure : il hérite du
         fond de page, comme la galerie et le bloc « Détails » de la fiche. -->
    <div class="mt-3">
      <p class="text-sm md:text-base font-semibold text-[#2d2e5c] dark:text-white line-clamp-2 leading-snug">
        {{ product.name }}
      </p>

      <p
        v-if="shortDescription"
        class="text-xs md:text-sm text-[#717198] dark:text-gray-400 mt-1 line-clamp-2"
      >
        {{ shortDescription }}
      </p>

      <!-- Coloris disponibles — décoratifs ici (le choix réel se fait sur la
           fiche), d'où l'`aria-hidden` et le libellé lecteur d'écran. -->
      <template v-if="colorSwatches.length > 0">
        <span class="sr-only">
          {{ $t("boutique.commun.colorisDisponibles", { liste: colorSwatches.map((s) => s.value).join(', ') }) }}
        </span>
        <div class="flex items-center gap-1.5 flex-wrap mt-2" aria-hidden="true">
          <span
            v-for="swatch in colorSwatches"
            :key="swatch.value"
            :title="swatch.value"
            class="size-4 rounded-full border border-gray-300 dark:border-[#3a3a3a]"
            :style="swatchStyle(swatch)"
          />
        </div>
      </template>

      <!-- PRIX en dernier et en gras : c'est l'information qu'on cherche en
           balayant une grille, elle doit toujours se trouver au même endroit,
           en bas du bloc. -->
      <p
        class="text-base md:text-lg font-bold mt-2 tabular-nums"
        :class="priceLabel === $t('boutique.commun.gratuit')
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-[#2d2e5c] dark:text-white'"
      >
        {{ priceLabel }}
      </p>
    </div>
  </NuxtLink>
</template>
